import { useCallback, useEffect, useMemo, useState } from 'react';
import './theme.css';
import LoginPage from './auth/LoginPage';
import { useStaffAuth } from './auth/useStaffAuth';
import { useConsoleData } from './useConsoleData';
import {
  CUTOVER,
  cutoverDone,
  hrefFor,
  initials,
  isAdmin,
  latestPerCheck,
  loadCutover,
  parseHash,
  pipelineOf,
  saveCutover,
  verdictOf,
  type Route,
} from './model';
import type { Ctx, DialField, DialKey } from './ctx';
import { CutoverList, Modal, Toast, VerdictChip } from './ui';
import Overview from './views/Overview';
import WebhooksList from './views/WebhooksList';
import WebhookDetail from './views/WebhookDetail';
import Tools from './views/Tools';
import type { DialPatch, WebhookEntry } from './types';

const NAVS: Array<[Route['view'], string, string]> = [
  ['overview', 'Overview', '1'],
  ['webhooks', 'Webhooks', '2'],
  ['tools', 'Tools', '3'],
];
const ENVS = ['sandbox', 'prod'];
const GITLAB = 'https://gitlab.com/optisigns/revops/os-revops-webhooks';

type Theme = 'dark' | 'light' | null;

interface Pending {
  key: DialKey;
  field: DialField;
  next: boolean;
  cutover?: boolean;
}

export default function App() {
  const auth = useStaffAuth();
  const data = useConsoleData();

  const [theme, setTheme] = useState<Theme>(() => {
    try {
      const t = localStorage.getItem('rw-theme');
      return t === 'dark' || t === 'light' ? t : null;
    } catch {
      return null;
    }
  });
  useEffect(() => {
    if (theme) document.documentElement.dataset.theme = theme;
    else document.documentElement.removeAttribute('data-theme');
    try {
      localStorage.setItem('rw-theme', theme ?? '');
    } catch {
      /* ignore */
    }
  }, [theme]);

  const [route, setRoute] = useState<Route>(() => parseHash(window.location.hash));
  useEffect(() => {
    const onHash = (): void => setRoute(parseHash(window.location.hash));
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);
  const go = useCallback((r: Route) => {
    window.location.hash = hrefFor(r);
  }, []);

  const [toastText, setToastText] = useState<string | null>(null);
  const toast = useCallback((text: string) => {
    setToastText(text);
    window.setTimeout(() => setToastText((t) => (t === text ? null : t)), 2600);
  }, []);

  const [cutover, setCutover] = useState<Record<string, boolean>>(() => loadCutover());
  const setCut = useCallback((key: string, checked: boolean) => {
    setCutover((prev) => {
      const next = { ...prev, [key]: checked };
      saveCutover(next);
      return next;
    });
  }, []);

  const [pending, setPending] = useState<Pending | null>(null);

  const service = data.service;
  const env = service?.deployEnv ?? (import.meta.env.VITE_ENV_NAME as string | undefined) ?? 'local';
  const portalId = (import.meta.env.VITE_HUBSPOT_PORTAL_ID as string | undefined) ?? '';
  const otherEnvUrl = (import.meta.env.VITE_OTHER_ENV_URL as string | undefined) ?? '';
  const admin = isAdmin(auth.user?.roles);

  const entries = useMemo<WebhookEntry[]>(() => data.webhooks?.webhooks ?? [], [data.webhooks]);
  const pipelines = useMemo(() => service?.pipelines ?? [], [service]);
  const pipelineFor = useCallback(
    (entry: WebhookEntry) => pipelineOf(entry, pipelines),
    [pipelines],
  );
  const ours = useMemo(
    () => entries.filter((e) => e.owner === 'ours' && e.pipeline),
    [entries],
  );
  const latest = useMemo(
    () => data.monitor?.checks ?? latestPerCheck(data.monitor?.events ?? []),
    [data.monitor],
  );
  const verdict = useCallback(
    (entry: WebhookEntry) => verdictOf(entry, pipelineFor(entry), data.dials, latest),
    [pipelineFor, data.dials, latest],
  );

  const applyDial = useCallback(
    async (key: DialKey, field: DialField, next: boolean) => {
      const patch: DialPatch =
        key === 'monitor' ? { monitor: { enabled: next } } : { [key]: { [field]: next } };
      try {
        await data.setDials(patch);
        toast(`PUT /api/dials {"${key}":{"${field}":${next}}} → ${env}`);
      } catch (e) {
        toast(e instanceof Error ? e.message : String(e));
      }
      setPending(null);
    },
    [data, env, toast],
  );

  const requestDial = useCallback(
    (key: DialKey, field: DialField, next: boolean) => {
      const isCutover = env === 'prod' && key === 'tts' && field === 'dryRun' && next === false;
      if (isCutover) return setPending({ key, field, next, cutover: true });
      if (env === 'prod') return setPending({ key, field, next });
      void applyDial(key, field, next);
    },
    [env, applyDial],
  );

  const ctx: Ctx = {
    data,
    env,
    admin,
    entries,
    ours,
    pipelineFor,
    latest,
    verdict,
    requestDial,
    toast,
    cutover,
    setCut,
    go,
    portalId,
  };

  // 1 / 2 / 3 jump between the top-level views (mockup parity).
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      const tag = document.activeElement?.tagName ?? '';
      if (e.metaKey || e.ctrlKey || ['INPUT', 'SELECT', 'TEXTAREA'].includes(tag)) return;
      const nav = NAVS.find((n) => n[2] === e.key);
      if (nav) go({ view: nav[0] } as Route);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [go]);

  if (!auth.user && !auth.disabled) return <LoginPage onCredential={auth.login} />;

  const activeTop =
    route.view === 'webhook' ? 'webhooks' : (route.view as 'overview' | 'webhooks' | 'tools');

  return (
    <>
      <div className="shell">
        <header className="top">
          <div className="brand">
            <span className="dot" /> RevOps Webhooks <span className="dim hide-sm">· console</span>
          </div>
          <div className="grow" />
          <div className="seg env" role="group" aria-label="Environment">
            {ENVS.map((e) => (
              <button
                key={e}
                type="button"
                className={`${e === env ? 'on ' : ''}${e === 'prod' ? 'prod' : ''}`}
                title={
                  e === env
                    ? `this console talks to ${e}`
                    : otherEnvUrl
                      ? `open the ${e} console`
                      : 'VITE_OTHER_ENV_URL not set'
                }
                disabled={e === env || !otherEnvUrl}
                onClick={() => {
                  if (otherEnvUrl) window.location.href = otherEnvUrl;
                }}
              >
                {e}
              </button>
            ))}
          </div>
          <span className="idchip" title="staff SSO (RS256 staff-auth JWT)">
            <span className="av">{initials(auth.user?.email ?? '')}</span>
            <span className="hide-sm">{auth.user?.email}</span>{' '}
            {auth.user?.roles?.[0] ? (
              <span className={`chip ${admin ? 'ok' : ''}`}>{auth.user.roles[0]}</span>
            ) : null}
          </span>
          <button
            type="button"
            className="btn sm"
            title="Toggle theme"
            onClick={() => setTheme(theme === 'dark' ? 'light' : theme === 'light' ? null : 'dark')}
          >
            ◐
          </button>
        </header>

        <nav className="nav">
          <div className="sep">Console</div>
          {NAVS.map(([view, label, key]) => (
            <button
              key={view}
              type="button"
              className={activeTop === view ? 'on' : ''}
              onClick={() => go({ view } as Route)}
            >
              {label}
              <span className="k">{key}</span>
            </button>
          ))}
          <div className="sep">Our webhooks</div>
          {ours.map((w) => (
            <button
              key={w.id}
              type="button"
              className={route.view === 'webhook' && route.id === w.id ? 'on' : ''}
              style={{ paddingLeft: 16 }}
              onClick={() =>
                go({
                  view: 'webhook',
                  id: w.id,
                  tab: route.view === 'webhook' && route.id === w.id ? route.tab : 'pipeline',
                })
              }
            >
              <VerdictChip v={verdict(w)} />
              {pipelineFor(w)?.name ?? w.id}
            </button>
          ))}
          <div className="sep">Links</div>
          <button type="button" onClick={() => window.open(GITLAB, '_blank', 'noopener')}>
            GitLab ↗
          </button>
          <button
            type="button"
            onClick={() => window.open('https://app.slack.com/client', '_blank', 'noopener')}
          >
            {service?.slackChannel ?? '#revops-webhooks'} ↗
          </button>
          <button
            type="button"
            disabled={!portalId}
            onClick={() =>
              window.open(`https://app.hubspot.com/contacts/${portalId}`, '_blank', 'noopener')
            }
          >
            HubSpot {env} ↗
          </button>
        </nav>

        <main>
          {route.view === 'overview' && <Overview ctx={ctx} />}
          {route.view === 'webhooks' && <WebhooksList ctx={ctx} />}
          {route.view === 'webhook' && <WebhookDetail ctx={ctx} id={route.id} tab={route.tab} />}
          {route.view === 'tools' && <Tools ctx={ctx} />}
        </main>
      </div>

      {pending?.cutover ? (
        <Modal
          title="Go live on prod — Talk to Sales"
          onClose={() => setPending(null)}
          actions={
            <>
              <button type="button" className="btn" onClick={() => setPending(null)}>
                Cancel
              </button>
              <button
                type="button"
                className="btn primary"
                disabled={!cutoverDone(cutover)}
                onClick={() => void applyDial(pending.key, pending.field, pending.next)}
              >
                {cutoverDone(cutover)
                  ? 'Flip to LIVE'
                  : `${CUTOVER.filter(([k]) => !cutover[k]).length} item(s) left`}
              </button>
            </>
          }
        >
          <p className="note">
            This turns <code>tts.dryRun</code> off: the next Webflow submission writes to HubSpot and
            Customer.io for real. The Zapier zap must be off at the same moment or every lead is
            processed twice.
          </p>
          <CutoverList state={cutover} onChange={setCut} />
        </Modal>
      ) : pending ? (
        <Modal
          title={
            <>
              Change{' '}
              <code>
                {pending.key}.{pending.field}
              </code>{' '}
              on <span className="chip warn">prod</span>?
            </>
          }
          onClose={() => setPending(null)}
          actions={
            <>
              <button type="button" className="btn" onClick={() => setPending(null)}>
                Cancel
              </button>
              <button
                type="button"
                className="btn primary"
                onClick={() => void applyDial(pending.key, pending.field, pending.next)}
              >
                Apply
              </button>
            </>
          }
        >
          <p className="note">
            {pending.key}.{pending.field}: <code>{String(!pending.next)}</code> →{' '}
            <code>{String(pending.next)}</code>.{' '}
            {pending.key === 'monitor' && !pending.next
              ? 'Nothing will be checked or posted until it is turned back on.'
              : pending.field === 'enabled' && !pending.next
                ? 'Incoming webhooks will be acknowledged and dropped (ledger: skipped:disabled). dial_drift will post within 5 minutes.'
                : ''}
          </p>
        </Modal>
      ) : null}

      {toastText ? <Toast text={toastText} /> : null}
    </>
  );
}
